import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { z } from 'zod'
import { useAuth } from '../lib/auth.js'
import { useBranding } from '../lib/branding.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { Icon } from '../components/Icon.js'

const loginSearchSchema = z.object({
  // `?role=parent` lands here from the marketing nav's "Parent sign in" link.
  // It only tweaks the welcome copy — auth itself is identical.
  role: z.enum(['parent', 'teacher', 'admin']).optional(),
})

export const Route = createFileRoute('/_auth/login')({
  validateSearch: loginSearchSchema,
  component: LoginPage,
})

function LoginPage() {
  const { signInWithPassword, signInWithGoogle } = useAuth()
  const { branding } = useBranding()
  const { role: roleHint } = useSearch({ from: '/_auth/login' })
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Default to email + google when no branding is loaded; respect academy
  // configuration when the request landed on a branded domain.
  const methods = branding?.loginMethods ?? ['email', 'google']
  const showEmail = methods.includes('email')
  const showGoogle = methods.includes('google')

  const isParentHint = roleHint === 'parent'

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await signInWithPassword(email, password)
      // Hard navigation (not a SPA navigate) so the next load re-runs the _app
      // `beforeLoad` gate against the freshly-set session cookie. A client-side
      // navigate races Better Auth's cached getSession: for ~2-3s /dashboard can
      // re-render the login form before the session resolves. Mirrors the
      // sign-OUT path, which uses window.location.assign for the same reason.
      window.location.assign('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-fg">
          {isParentHint ? 'Parent sign in' : 'Sign in'}
        </h1>
        <p className="text-sm text-fg-muted">
          {branding
            ? `Welcome to ${branding.name}.`
            : isParentHint
              ? 'Use the email your academy sent your invite to.'
              : 'Welcome back to your academy.'}
        </p>
      </header>

      {/* Universal hint — only shown when no per-academy branding has loaded,
          because branded login pages already imply the audience. */}
      {!branding && !isParentHint ? (
        <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted px-3 py-2 text-xs text-fg-muted">
          <Icon name="users" size={14} className="mt-0.5 text-fg-subtle" />
          <p>
            Parents and teachers: use the email your academy sent your invite to.
            No separate password needed.
          </p>
        </div>
      ) : null}

      {showEmail ? (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@academy.com"
            />
          </Field>
          <div className="space-y-1.5">
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </Field>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-[var(--color-primary)] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {error ? (
            <div className="rounded-md border border-danger-border bg-danger-tint px-3 py-2 text-sm text-danger">
              {error}
            </div>
          ) : null}

          <Button type="submit" loading={submitting} className="w-full">
            Sign in
          </Button>
        </form>
      ) : null}

      {showEmail && showGoogle ? (
        <div className="relative flex items-center">
          <span className="flex-1 h-px bg-border" />
          <span className="px-3 text-xs text-fg-subtle">or</span>
          <span className="flex-1 h-px bg-border" />
        </div>
      ) : null}

      {showGoogle ? (
        <Button onClick={signInWithGoogle} variant="secondary" className="w-full">
          Continue with Google
        </Button>
      ) : null}

      {showEmail ? (
        <p className="text-center text-sm text-fg-muted">
          New academies join by invitation. Parents and teachers: sign in with
          the email your academy invited you with.
        </p>
      ) : null}
    </div>
  )
}
